import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import {
  absoluteUrl,
  breadcrumbSchema,
  itemListSchema,
  JsonLd,
} from '../../components/JsonLd'
import { Section } from '../../components/Section'
import { SectionLastCTA } from '../../components/SectionLastCTA'
import { SlitePage } from '../../components/SlitePage'
import { useRoutes } from '../../routes'
import type { Data } from './+data'
import { filterIntegrations } from './filterIntegrations'
import { IntegrationGrid } from './IntegrationGrid'
import { IntegrationsHeader } from './IntegrationsHeader'
import { IntegrationsSidebar } from './IntegrationsSidebar'

export default function IntegrationsPage() {
  const intl = useIntl()
  const routes = useRoutes()

  const { integrations } = useData<Data>()
  const [searchQuery, setSearchQuery] = useState('')

  const filteredIntegrations = useMemo(
    () => filterIntegrations(integrations, searchQuery),
    [integrations, searchQuery]
  )

  const pageTitle = intl.formatMessage({
    id: 'integrations.page.title',
    defaultMessage: 'Integrations',
  })

  const pageDescription = intl.formatMessage({
    id: 'integrations.page.description',
    defaultMessage:
      'Connect your favorite tools to Slite to enhance your knowledge base experience',
  })

  const itemList = itemListSchema({
    items: integrations.map((integration) => ({
      name: integration.name,
      url: absoluteUrl(
        routes.integrations.detail({ slug: integration.slug.current })
      ),
    })),
  })

  const breadcrumbs = breadcrumbSchema({
    items: [
      { name: 'Slite', url: absoluteUrl('/') },
      { name: pageTitle, url: absoluteUrl(routes.integrations.index()) },
    ],
  })

  return (
    <SlitePage
      canonicalUrl={routes.integrations.index()}
      background="moon-dust-lighter"
      title={pageTitle}
      description={pageDescription}
      ogImage="https://cdn.prod.website-files.com/5f84417443f34a30444f52d3/66275fa84fcd84a30d551c6e_Open%20graph%20-%20Integrations.jpg"
    >
      <JsonLd data={itemList} />
      <JsonLd data={breadcrumbs} />
      <IntegrationsHeader
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
      />
      <Section background="moon-dust-lighter" containerTopPadding={0}>
        <div className="grid-12 grid-gap-40">
          <div className="grid-span-3 grid-span-12-small">
            <IntegrationsSidebar current="all" />
          </div>
          <div className="grid-span-9 grid-span-12-small">
            <IntegrationGrid integrations={filteredIntegrations} columns={3} />
          </div>
        </div>
      </Section>
      <SectionLastCTA background="moon-dust-lighter" />
    </SlitePage>
  )
}
